import { useTranslation } from 'react-i18next';
import { useEffect, useState, useCallback, useMemo, createContext, useContext } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// sessionStorage key — preview state survives reloads but not browser
// close + reopen, and never crosses login boundaries (cleared on logout).
const VIEW_AS_KEY = 'expertfix:view_as_role';

const PermissionsContext = createContext(null);

/**
 * Provider that fetches /api/permissions/me once after auth and exposes
 * `{ permissions: Set, role, label, isSuperadmin, can(key), viewAsRole, setViewAsRole }`.
 *
 * Admins/superadmins can call `setViewAsRole('auditor')` to preview the
 * UI as another role; the hook re-fetches with `?as_role=auditor` so
 * every gated tab/button reflects that role's permission set. Calling
 * `setViewAsRole(null)` exits preview.
 */
export const PermissionsProvider = ({ token, children }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewAsRole, setViewAsRoleState] = useState(() => {
    try { return sessionStorage.getItem(VIEW_AS_KEY) || null; } catch { return null; }
  });
  // Cache the user's REAL permissions (admin/superadmin powers) so the
  // matrix can keep working even while previewing as a low-privilege
  // role — otherwise the admin would lose access to the very picker
  // they used to enter preview mode.
  const [realData, setRealData] = useState(null);

  const fetchMine = useCallback(async () => {
    if (!token) {
      setData(null); setRealData(null); setLoading(false);
      return;
    }
    try {
      const realResp = await axios.get(`${API}/permissions/me`);
      setRealData(realResp.data);
      if (viewAsRole) {
        try {
          const previewResp = await axios.get(`${API}/permissions/me`, { params: { as_role: viewAsRole } });
          setData(previewResp.data);
        } catch {
          // Preview failed (e.g. lost admin rights mid-session) — drop preview
          setData(realResp.data);
          setViewAsRoleState(null);
          try { sessionStorage.removeItem(VIEW_AS_KEY); } catch { /* ignore */ }
        }
      } else {
        setData(realResp.data);
      }
    } catch {
      setData(null); setRealData(null);
    } finally {
      setLoading(false);
    }
  }, [token, viewAsRole]);

  useEffect(() => { setLoading(true); fetchMine(); }, [fetchMine]);

  const setViewAsRole = useCallback((role) => {
    try {
      if (role) sessionStorage.setItem(VIEW_AS_KEY, role);
      else sessionStorage.removeItem(VIEW_AS_KEY);
    } catch { /* ignore quota errors */ }
    setViewAsRoleState(role || null);
  }, []);

  const value = useMemo(() => {
    const perms = new Set(data?.permissions || []);
    const isSuperadmin = !!data?.is_superadmin;
    const realIsAdmin = !!realData?.is_superadmin || realData?.role === 'admin';
    return {
      permissions: perms,
      role: data?.role || null,
      label: data?.label || '',
      isSuperadmin,
      loading,
      can: (key) => isSuperadmin || perms.has(key),
      canAny: (...keys) => isSuperadmin || keys.some((k) => perms.has(k)),
      reload: fetchMine,
      // Preview state — components use these to render the banner +
      // gate the picker (admins only).
      viewAsRole,
      setViewAsRole,
      realRole: realData?.role || null,
      realIsAdmin,
    };
  }, [data, realData, loading, fetchMine, viewAsRole, setViewAsRole]);

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    return {
      permissions: new Set(),
      role: null,
      label: '',
      isSuperadmin: false,
      loading: false,
      can: () => false,
      canAny: () => false,
      reload: () => {},
      viewAsRole: null,
      setViewAsRole: () => {},
      realRole: null,
      realIsAdmin: false,
    };
  }
  return ctx;
};

export default usePermissions;
